import { useCallback, useEffect, useRef, useState } from "react";

// Keep request state local to its form, row, or drawer. Leaving that scope also
// cancels fetchClient's authentication retry and ignores any late response.
const useEmailDomainRequest = (scope) => {
  const request = useRef(null);
  const mounted = useRef(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    mounted.current = true;
    setBusy(false);
    setErrors({});
    return () => {
      mounted.current = false;
      request.current?.abort();
      request.current = null;
    };
  }, [scope]);

  const run = useCallback(async (operation) => {
    if (!mounted.current || request.current) {
      return undefined;
    }
    const controller = new AbortController();
    request.current = controller;
    const isRequestCurrent = () =>
      mounted.current &&
      request.current === controller &&
      !controller.signal.aborted;
    setBusy(true);
    setErrors({});
    try {
      const result = await operation({
        signal: controller.signal,
        isRequestCurrent,
      });
      return isRequestCurrent() ? result : undefined;
    } catch (error) {
      if (isRequestCurrent() && error?.name !== "AbortError") {
        const details = error?.errors;
        if (typeof details === "string" || Array.isArray(details)) {
          setErrors({ base: [details].flat() });
        } else {
          setErrors(
            details || {
              base: [
                error?.message ||
                  "Unable to update sending domains. Please try again.",
              ],
            },
          );
        }
      }
      return undefined;
    } finally {
      if (isRequestCurrent()) {
        request.current = null;
        setBusy(false);
      }
    }
  }, []);

  return { run, busy, errors, setErrors };
};

export default useEmailDomainRequest;
